/**
 * The restricted / organization / link control, with the organization's role.
 *
 * Extracted from ShareModal so the default-sharing setting offers the same
 * choice (#11877). Purely presentational.
 */

import React from "react";
import { Buildings, Link as LinkIcon, LockKey } from "@phosphor-icons/react";
import InkSelect from "../InkSelect";
import type { GeneralAccess } from "./shareParts";

interface GeneralAccessControlProps {
  value: GeneralAccess;
  orgPermission: "view" | "edit";
  /** The organization's name, shown as the option's label. */
  organizationName: string;
  showLinkSharing: boolean;
  canManage: boolean;
  allowOrgEdit?: boolean;
  /** Sentence under the control saying what the current choice means. */
  caption: string;
  onChange: (value: GeneralAccess) => void;
  onOrgPermissionChange: (permission: "view" | "edit") => void;
}

const GeneralAccessControl: React.FC<GeneralAccessControlProps> = ({
  value,
  orgPermission,
  organizationName,
  showLinkSharing,
  canManage,
  allowOrgEdit = true,
  caption,
  onChange,
  onOrgPermissionChange,
}) => (
  <div>
    <h3
      className="uppercase tracking-wider text-[var(--fp-ink-3)] mb-3"
      style={{ fontSize: "var(--fp-t-fine)", fontWeight: 600 }}
    >
      General access
    </h3>
    <div className="flex items-center gap-2">
      {value === "restricted" ? (
        <LockKey
          size={20}
          weight="regular"
          className="text-[var(--fp-ink-2)] flex-shrink-0"
        />
      ) : value === "organization" ? (
        <Buildings
          size={20}
          weight="regular"
          className="text-[var(--fp-ink-2)] flex-shrink-0"
        />
      ) : (
        <LinkIcon
          size={20}
          weight="regular"
          className="text-[var(--fp-ink-2)] flex-shrink-0"
        />
      )}
      {canManage ? (
        <InkSelect<GeneralAccess>
          value={value}
          options={[
            { value: "restricted", label: "Restricted" },
            { value: "organization", label: organizationName },
            ...(showLinkSharing
              ? ([
                  {
                    value: "link",
                    label: "Anyone with the link",
                  },
                ] as const)
              : []),
          ]}
          onChange={onChange}
          ariaLabel="General access"
          wrapLabel
          className="flex-1"
        />
      ) : (
        <span className="flex-1 text-sm font-medium text-[var(--fp-ink)] truncate">
          {value === "organization"
            ? organizationName
            : value === "link"
              ? "Anyone with the link"
              : "Restricted"}
        </span>
      )}
      {value === "organization" &&
        (canManage && allowOrgEdit ? (
          <InkSelect<"view" | "edit">
            value={orgPermission}
            options={[
              { value: "view", label: "Viewer" },
              { value: "edit", label: "Editor" },
            ]}
            onChange={onOrgPermissionChange}
            ariaLabel="Organization role"
          />
        ) : (
          <span className="text-sm font-medium text-[var(--fp-ink)]">
            {orgPermission === "view" ? "Viewer" : "Editor"}
          </span>
        ))}
    </div>
    <p className="text-xs text-[var(--fp-ink-3)] mt-2 ml-8">{caption}</p>
  </div>
);

export default GeneralAccessControl;
